import { Divider } from "primereact/divider";
import React, { useEffect, useRef, useState } from "react";
import { getCountryName } from "../helpers/getCountryName";
import { getLanguageOptions } from "../helpers/languageHelper";
import { getI18nLanguageFromLocalStorage, getCurrentUserFromLocalStorage } from "../helpers/localStorageHelper";
import useCurrency from "../hook/useCurrency";
import { translate } from "../localization/translateService";
import { getBundleDiscounts } from "../services/bundleDiscountService";
import { setFavoriteTrainer } from "../services/favoriteService";
import { getCountry } from "../services/flag";
import { getAvailableDays, getAvailableInstructorStyles, getAvailableTags, getTrainersLanding } from "../services/trainersService";
import { convertCurrency } from "../helpers/currency";
import NewTrainerItemV2 from "./trainer/components/NewTrainerItemV2";
import TrainerDetailModal from "./trainer/TrainerDetailModal";
import TrainerHeroSection from "./TrainerHeroSection";
import KeduLoading from "./loading/KeduLoading";
import { getAllAvailableDirectDeliveryCoupon } from "../services/couponService";
import { COUPON_TARGETS } from "../constants/coupon-targets";
const FEE_RANGE_BY_CURRENCY = {
  KRW: { min: 0, max: 1_400_000, step: 10_000 },
  USD: { min: 0, max: 1_000, step: 50 },
  VND: { min: 0, max: 25_000_000, step: 100_000 },
};

const TrainerList = ({ props, messages }) => {
  const { defaultCurrency, ratesData, target: currentCurrency } = useCurrency();

  // Helper function to map URL values to category-grouped format
  const mapValuesToCategories = (urlValues, optionsData) => {
    if (!urlValues || !urlValues.length || !optionsData) return [];

    const result = [];
    const categoryMap = {};

    // Group values by category
    urlValues.forEach((urlValue) => {
      Object.keys(optionsData).forEach((categoryKey) => {
        const categoryItems = optionsData[categoryKey];
        const foundItem = categoryItems.find((item) => item.key === urlValue);
        if (foundItem) {
          const categoryName = categoryKey.toLowerCase();
          if (!categoryMap[categoryName]) {
            categoryMap[categoryName] = [];
          }
          categoryMap[categoryName].push(foundItem.value);
        }
      });
    });

    // Convert to required format
    Object.keys(categoryMap).forEach((categoryName) => {
      result.push({
        name: categoryName,
        value: categoryMap[categoryName],
      });
    });

    return result;
  };
  const [listCountryOption, setListCountryOption] = useState([]);
  const [tagsOption, setTagsOption] = useState(null);
  const [countryOption, setCountryOption] = useState([]);
  const [lang, setLanguage] = useState("");
  const [instructorStylesOption, setInstructorStylesOption] = useState(null);
  const [daysOption, setDaysOption] = useState([]);
  const [hasGetAllConfigDone, setHasGetAllConfigDone] = useState(false);
  const [hasGetAllOptionsDone, setHasGetAllOptionsDone] = useState(false);
  const [languages, setLanguages] = useState([]);
  const currentUser = getCurrentUserFromLocalStorage();

  const [searchSidebarVisible, setSearchSidebarVisible] = useState(false);

  const [searchFilterFixedButtonVisible, setSearchFilterFixedButtonVisible] = useState(false);

  const searchFilterWrapperRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!searchFilterWrapperRef.current) return;

      const rect = searchFilterWrapperRef.current.getBoundingClientRect();

      if (rect.bottom < 0) {
        setSearchFilterFixedButtonVisible(true);
      } else {
        setSearchFilterFixedButtonVisible(false);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const language = getI18nLanguageFromLocalStorage();
    setLanguage(language);
  }, [lang]);

  useEffect(() => {
    if (languages.length > 0 && listCountryOption.length > 0 && tagsOption != null && instructorStylesOption != null && daysOption.length > 0) {
      setHasGetAllOptionsDone(true);
    }
  }, [listCountryOption, tagsOption, instructorStylesOption, daysOption, languages]);

  // Initialize states from URL parameters
  const getInitialStateFromURL = (tagsOptionData = null, instructorStylesOptionData = null) => {
    const urlParams = new URLSearchParams(window.location.search);
    const rowsFromUrl = parseInt(urlParams.get("rows")) || 8;

    const selectedPriceValue =
      urlParams.get("startTuition") && urlParams.get("endTuition")
        ? {
          min: convertCurrency(parseInt(urlParams.get("startTuition")), defaultCurrency, currentCurrency, ratesData),
          max: convertCurrency(parseInt(urlParams.get("endTuition")), defaultCurrency, currentCurrency, ratesData),
        }
        : null;

    const selectedCourseContentValue = urlParams.get("courseContent") ? urlParams.get("courseContent").split(",") : [];

    const selectedTeacherStyleValue = urlParams.get("teacherStyle") ? urlParams.get("teacherStyle").split(",") : [];

    // Map course content using tagsOption if available
    const mappedCourseContent = tagsOptionData && selectedCourseContentValue.length > 0 ? mapValuesToCategories(selectedCourseContentValue, tagsOptionData) : [];

    // Map teacher style using instructorStylesOption if available
    const mappedTeacherStyle =
      instructorStylesOptionData && selectedTeacherStyleValue.length > 0 ? mapValuesToCategories(selectedTeacherStyleValue, instructorStylesOptionData) : [];

    return {
      first: (parseInt(urlParams.get("page")) - 1) * rowsFromUrl || 0,
      rows: rowsFromUrl,
      selectedTime: urlParams.get("startTime") && urlParams.get("endTime") ? { startTime: urlParams.get("startTime"), endTime: urlParams.get("endTime") } : null,
      selectedDay: urlParams.get("dayAvailable") ? urlParams.get("dayAvailable").split(",") : null,
      selectedPrice: selectedPriceValue,
      selectedCourseContent: mappedCourseContent,
      sortBy: urlParams.get("sortBy") || null,
      selectCountry: urlParams.get("nationality") ? urlParams.get("nationality").split(",") : null,
      selectedGender: urlParams.get("gender") ? urlParams.get("gender").split(",") : [],
      selectedSpokenLanguage: urlParams.get("languageSpoken") ? urlParams.get("languageSpoken").split(",") : null,
      selectedTeacherStyle: mappedTeacherStyle,
    };
  };

  useEffect(() => {
    if (defaultCurrency && currentCurrency && ratesData && hasGetAllOptionsDone) {
      const initialState = getInitialStateFromURL(tagsOption, instructorStylesOption);
      setFirst(initialState.first);
      setRows(initialState.rows);
      setSelectedTime(initialState.selectedTime);
      setSelectedDay(initialState.selectedDay);
      setSelectedPrice(initialState.selectedPrice);
      setSelectedCourseContent(initialState.selectedCourseContent);
      setSortBy(initialState.sortBy);
      setSelectCountry(initialState.selectCountry);
      setSelectedGender(initialState.selectedGender);
      setSelectedSpokenLanguage(initialState.selectedSpokenLanguage);
      setSelectedTeacherStyle(initialState.selectedTeacherStyle);
      setHasGetAllConfigDone(true);
    }
  }, [defaultCurrency, currentCurrency, ratesData, tagsOption, instructorStylesOption, hasGetAllOptionsDone]);

  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(8);
  const [totalRecords, setTotalRecords] = useState(1);
  const [trainerList, setTrainerList] = useState(null);
  const trainerDetailModalRef = React.useRef(null);

  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedDay, setSelectedDay] = useState([]);
  const [selectedPrice, setSelectedPrice] = useState(null);
  const [selectedCourseContent, setSelectedCourseContent] = useState([]);
  const [sortBy, setSortBy] = useState(null);
  const [selectCountry, setSelectCountry] = useState(null);
  const [selectedGender, setSelectedGender] = useState([]);
  const [selectedSpokenLanguage, setSelectedSpokenLanguage] = useState(null);
  const [selectedTeacherStyle, setSelectedTeacherStyle] = useState(null);
  const [bundleDiscounts, setBundleDiscounts] = useState([]);
  const [directDeliveryCoupons, setDirectDeliveryCoupons] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const sentinelRef = useRef(null);

  const [hasFiltering, setHasFiltering] = useState(false);
  const [activeTooltipId, setActiveTooltipId] = useState(null);

  const { trainerCard = {}, trainerItemProps = {}, filter = {} } = props;

  const filterValue = filter.filterValue || {};

  const {
    min: displayMin,
    max: displayMax,
    step: displayStep,
  } = FEE_RANGE_BY_CURRENCY[currentCurrency] ?? {
    min: 0,
    max: 50_000,
    step: 1_000,
  };

  const getPriceRangeInDefaultCurrency = () => {
    if (!selectedPrice) return null;
    const minDefault = convertCurrency(selectedPrice.min, currentCurrency, defaultCurrency, ratesData);
    const maxDefault = convertCurrency(selectedPrice.max, currentCurrency, defaultCurrency, ratesData);
    return {
      min: Math.round(minDefault),
      max: Math.round(maxDefault),
    };
  };

  const sortRequest = () => {
    switch (sortBy) {
      case props.filter.filterLabel.topRecommendedSortLabel:
        return [
          {
            field: "rating",
            order: -1,
          },
        ];
      case props.filter.filterLabel.mostReviewsSortLabel:
        return [
          {
            field: "numberRating",
            order: -1,
          },
        ];
      case props.filter.filterLabel.highestRatingSortLabel:
        return [
          {
            field: "numberRating",
            order: -1,
          },
          {
            field: "rating",
            order: -1,
          },
        ];
      case props.filter.filterLabel.highestPriceSortLabel:
        return [
          {
            field: "price",
            order: -1,
          },
        ];
      case props.filter.filterLabel.lowestPriceSortLabel:
        return [
          {
            field: "price",
            order: 1,
          },
        ];
      case props.filter.filterLabel.byPopularitySortLabel:
        return [
          {
            field: "numberAttendances",
            order: -1,
          },
        ];
      default:
        return [];
    }
  };

  const updateUrlParams = (body, query) => {
    const url = new URL(window.location);
    const params = url.searchParams;

    // Keep existing non-filter params (like 'tab')
    const nonFilterParams = ["tab"];
    const existingParams = {};
    nonFilterParams.forEach((param) => {
      if (params.has(param)) {
        existingParams[param] = params.get(param);
      }
    });

    // Clear existing filter params only
    [
      "page",
      "rows",
      "sortBy",
      "dayAvailable",
      "startTime",
      "endTime",
      "nationality",
      "startTuition",
      "endTuition",
      "gender",
      "courseContent",
      "languageSpoken",
      "teacherStyle",
      "search"
    ].forEach((param) => {
      params.delete(param);
    });

    // Restore non-filter params
    Object.entries(existingParams).forEach(([key, value]) => {
      params.set(key, value);
    });

    // Add pagination params
    // params.set("page", (Math.floor(body.first / rows) + 1).toString());
    // params.set("rows", rows.toString());

    // Add sort params
    if (sortBy) {
      params.set("sortBy", sortBy);
    }

    // Add filter params
    if (query.dayAvailable) params.set("dayAvailable", query.dayAvailable);
    if (query.startTimeAvailable) params.set("startTime", query.startTimeAvailable);
    if (query.endTimeAvailable) params.set("endTime", query.endTimeAvailable);
    if (query.nationality) params.set("nationality", query.nationality);
    if (query.startTuition) params.set("startTuition", query.startTuition.toString());
    if (query.endTuition) params.set("endTuition", query.endTuition.toString());
    if (query.gender) params.set("gender", query.gender);
    if (query.courseContent) params.set("courseContent", query.courseContent);
    if (query.languageSpoken) params.set("languageSpoken", query.languageSpoken);
    if (query.teacherStyle) params.set("teacherStyle", query.teacherStyle);
    if (query.search) params.set("search", query.search);
    window.history.pushState({}, "", url);
  };

  const handleFavoriteTrainer = async (publicId) => {
    setFavoriteTrainer(publicId).then((response) => {
      if (response.status === 200) {
        const trainerIndex = trainerList.findIndex((trainer) => trainer.publicId === publicId);
        if (trainerIndex !== -1) {
          const updatedTrainer = {
            ...trainerList[trainerIndex],
            isFavorite: !trainerList[trainerIndex].isFavorite,
          };
          const updatedTrainerList = [...trainerList];
          updatedTrainerList[trainerIndex] = updatedTrainer;
          setTrainerList(updatedTrainerList);
        }
      }
    });
  };
  const getTrainerList = async (hasFilter = true, firstOffset, appendMode = false) => {
    const offset = firstOffset !== undefined ? firstOffset : first;

    if (appendMode) setIsLoadingMore(true);

    const paginationBody = {
      first: offset,
      rows: rows,
      filters: null,
      multiSortMeta: [...sortRequest()],
    };

    const courseContentFilterValues = selectedCourseContent?.flatMap((item) => item.value);
    const courseContentFilter = courseContentFilterValues?.join(",");

    const teacherStyleFilter = selectedTeacherStyle?.map((item) => item.value)?.join(",");

    const rangeDefault = getPriceRangeInDefaultCurrency();

    const filterRequest = {
      dayAvailable: selectedDay?.join(","),
      startTimeAvailable: selectedTime?.startTime ? selectedTime.startTime : null,
      endTimeAvailable: selectedTime?.endTime ? selectedTime.endTime : null,
      nationality: selectCountry?.join(","),
      startTuition: rangeDefault ? rangeDefault.min : null,
      endTuition: rangeDefault ? rangeDefault.max : null,
      gender: selectedGender?.join(","),
      courseContent: courseContentFilter,
      languageSpoken: selectedSpokenLanguage?.join(","),
      teacherStyle: teacherStyleFilter,
      search: searchText ? searchText : null,
    };

    // Update URL parameters while preserving non-filter params like 'tab'
    updateUrlParams(paginationBody, filterRequest);

    try {
      const response = hasFilter ? await getTrainersLanding(paginationBody, filterRequest) : await getTrainersLanding(paginationBody);

      const dataSources = response.data;
      const translationPromises = [];
      dataSources.forEach((dataItem) => {
        if (dataItem.trainerLanguages && dataItem.trainerLanguages.length > 0) {
          const languageTranslationPromises = dataItem.trainerLanguages.map(async (lang) => {
            lang.proficiencyDescription = await translate(lang.proficiencyDescription, true);
            return lang;
          });
          translationPromises.push(Promise.all(languageTranslationPromises));
        }

        if (dataItem.trainerTags && dataItem.trainerTags.length > 0) {
          const trainerTagTranslationPromises = dataItem.trainerTags.map(async (tag) => {
            tag.description = await translate(tag.description, true);
            return tag;
          });
          translationPromises.push(Promise.all(trainerTagTranslationPromises));
        }
      });

      await Promise.all(translationPromises);

      const total = response.pagingInfo.totalRecordCount;
      setTotalRecords(total);

      if (appendMode) {
        setTrainerList((prev) => [...(prev || []), ...dataSources]);
        setHasMore(offset + dataSources.length < total);
        setIsLoadingMore(false);
      } else {
        setTrainerList(dataSources);
        setHasMore(dataSources.length < total);
      }
    } catch (error) {
      if (!appendMode) {
        setTrainerList([]);
        setTotalRecords(0);
      }
      setHasMore(false);
      setIsLoadingMore(false);
    }
  };

  const translateCountries = (countries) => {
    var map = [];
    countries.forEach((country) => {
      var translated = getCountryName(country.code);
      map.push({ ...country, name: translated });
    });

    return map;
  };

  useEffect(() => {
    const getAllOptions = async () => {
      const language = getI18nLanguageFromLocalStorage();
      const [tags, countries, instructorStyles, availableDays, languageOptions, bundleDiscounts, directDeliveryCoupons] = await Promise.all([
        getAvailableTags(),
        getCountry(),
        getAvailableInstructorStyles(),
        getAvailableDays(),
        getLanguageOptions(language),
        getBundleDiscounts(),
        getAllAvailableDirectDeliveryCoupon()
      ]);
      // setRows(props.filter.rowPerPage);

      setBundleDiscounts(bundleDiscounts);
      setDirectDeliveryCoupons(directDeliveryCoupons);
      const translatedCountries = translateCountries(countries);
      setListCountryOption(translatedCountries);
      if (translateCountries.length === 0) {
      }
      setCountryOption(translatedCountries);
      setLanguages(languageOptions);
      const translatedTags = await translateData(tags);
      setTagsOption(translatedTags);
      const translatedInstructorStyles = await translateData(instructorStyles);
      setInstructorStylesOption(translatedInstructorStyles);
      const translatedDaysData = await translateData(availableDays, true);
      setDaysOption(translatedDaysData?.WEEKDAY);
    };
    getAllOptions();
  }, []);

  const translateData = async (originalData, keepKey = false) => {
    const translatedData = {};
    for (const key of Object.keys(originalData)) {
      const keyString = "COMMON_ITEMS." + key;
      let newKey = await translate(keyString, true);

      const newValue = await Promise.all(
        originalData[key].map(async (item) => {
          const name = await translate(item.key);
          const value = await translate(item.value);
          return {
            key: name,
            value: value,
          };
        })
      );

      if (keepKey) {
        translatedData[key] = newValue;
      } else {
        translatedData[newKey] = newValue;
      }
    }

    return translatedData;
  };

  useEffect(() => {
    if (hasGetAllConfigDone && hasGetAllOptionsDone) {
      getTrainerList();
    }
  }, [hasGetAllConfigDone, hasGetAllOptionsDone]);

  const onFilter = () => {
    setHasFiltering(true);
    setFirst(0);
    setHasMore(true);
    setTrainerList(null);
    getTrainerList(true, 0, false);
  };

  const onReset = () => {
    setHasFiltering(false);
    setFirst(0);
    setHasMore(true);
    setTrainerList(null);
    setSelectedTime(null);
    setSelectedDay(null);
    setSelectedPrice(null);
    setSelectedCourseContent([]);
    setSortBy(null);
    setSelectCountry(null);
    setSelectedGender([]);
    setSelectedSpokenLanguage(null);
    setSelectedTeacherStyle(null);
    getTrainerList(false, 0, false);
  };

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !isLoadingMore && trainerList !== null) {
          const nextFirst = first + rows;
          setFirst(nextFirst);
          getTrainerList(true, nextFirst, true);
        }
      },
      { rootMargin: "300px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, isLoadingMore, first, trainerList, rows]);

  const filterProps = {
    onFilterDayChange: setSelectedDay,
    onFilterTimeChange: setSelectedTime,
    onFilterPriceChange: setSelectedPrice,
    onFilterCourseContentChange: setSelectedCourseContent,
    onSortOptionChange: setSortBy,
    onFilterCountryChange: setSelectCountry,
    onFilterGenderChange: setSelectedGender,
    onFilterLanguageChange: setSelectedSpokenLanguage,
    onFilterStyleChange: setSelectedTeacherStyle,
    onFilterClick: onFilter,
    onFilterReset: onReset,
    onSearchTextChange: setSearchText,
    selectedTime: selectedTime,
    selectedDay: selectedDay,
    selectedPrice: selectedPrice,
    selectedCourseContent: selectedCourseContent,
    sortBy: sortBy,
    selectCountry: selectCountry,
    selectedGender: selectedGender,
    selectedSpokenLanguage: selectedSpokenLanguage,
    selectedTeacherStyle: selectedTeacherStyle,
    languages: languages,
    translationData: filter,
    listCountryOption: listCountryOption,
    tuitionFeeRangeMinValue: displayMin,
    tuitionFeeRangeMaxValue: displayMax,
    tuitionFeeRangeStepValue: displayStep,
    filterValue: filterValue,
    trainerCard: trainerCard,
    trainerItemProps: trainerItemProps,
    currency: currentCurrency,
    ratesData: ratesData,
    defaultCurrency: defaultCurrency,
    searchText: searchText,
  };

  const renderTrainnerList = () => {
    return (
      <div id="trainer-list-item">
        {trainerList.length > 0 ? (
          <div className="grid">
            {hasFiltering && (
              <div className="w-full flex keduall-typography heading-h4-bold-700">
                {totalRecords} {props.lectureListSearchResult}
              </div>
            )}
            {trainerList.map((trainer, index) => {
              return (
                <div key={trainer.id} className="col-12 md:col-4 lg:col-3">
                  <NewTrainerItemV2
                    props={props.trainerItemProps}
                    key={trainer.id}
                    source={trainer}
                    bundleDiscounts={bundleDiscounts}
                    isActiveDirectDeliveryCoupons={directDeliveryCoupons.some(coupon => coupon.targetContentType === COUPON_TARGETS.TRAINER && (coupon.applyFor.length === 0 || coupon.applyFor.some(apply => apply === trainer.publicId)))}
                    activeTooltipId={activeTooltipId}
                    onItemHover={(trainerId) => setActiveTooltipId(trainerId)}
                    onViewMoreClick={() => {
                      window.location.href = `${window.location.pathname}/${trainer.publicId}`;
                    }}
                    onFavoriteClick={() => {
                      handleFavoriteTrainer(trainer.publicId);
                    }}
                    lang={lang}
                    currentUser={currentUser}
                    messages={messages}
                  />
                </div>
                );
            })
            }
          </div>
        ) : (
          <div className="flex flex-column gap-5">
            <div className="flex flex-column">
              <div className="w-full flex justify-content-center keduall-typography heading-h2-bold-700">{props.lectureListNotFoundHeading1}</div>
              <div className="w-full flex justify-content-center keduall-typography heading-h4-normal-400 mt-3">{props.lectureListNotFoundHeading2}</div>
            </div>
            <div className="flex flex-row justify-content-center align-items-center p-lecture-not-found flex-wrap md:flex-nowrap">
              <div>
                <img alt="not_found_1" src={props.lectureListNotFoundPicture1} />
              </div>
              <div className="flex flex-column ml-3">
                <div className="w-full flex justify-content-center keduall-typography heading-h4-bold-700">{props.lectureListNotFoundContent1}</div>
                <div className="w-full flex justify-content-center keduall-typography heading-h4-bold-700">{props.lectureListNotFoundContent2}</div>
              </div>
              <div>
                <img alt="not_found_2" src={props.lectureListNotFoundPicture2} />
              </div>
            </div>
          </div>
        )}
        {isLoadingMore && (
          <div className="w-full flex justify-content-center p-3">
            <KeduLoading />
          </div>
        )}
        <div ref={sentinelRef} />
      </div>
    );
  };

  return (
    <div>
      {(hasGetAllConfigDone && hasGetAllOptionsDone) ?
        <>
          <div className="flex w-full align-content-center justify-content-center mt-6">
            <h3 className="lg:diff-color-50 flex w-max align-content-center justify-content-center keduall-typography heading-h3-bold-700">
              {filterProps.translationData.headerPanel}
            </h3>
          </div>
          {/* <div ref={searchFilterWrapperRef}>
            <TrainerHeroSection
              props={filterProps}
              tagsOption={tagsOption}
              instructorStylesOption={instructorStylesOption}
              daysOption={daysOption}
              usingSidebar={true}
              searchSidebarVisible={searchSidebarVisible}
              onCloseSearchSidebar={() => setSearchSidebarVisible(false)}
            />
            {searchFilterFixedButtonVisible && (
              <div
                className="bg-buttons-primary border-round-right-sm flex justify-content-center align-items-center search-filter-fixed-button cursor-pointer"
                onClick={() => setSearchSidebarVisible(true)}
              >
                <i className="pi pi-search white-color text-2xl"></i>
              </div>
            )}
          </div> */}
          <Divider className="keduall-divider" />
          <div className="keduall-form flex flex-column gap-5 w-full">
            <TrainerDetailModal ref={trainerDetailModalRef} defaultAvatar={props.trainerCard.trainerAvatar} />
            {trainerList ? renderTrainnerList() : <KeduLoading />}
          </div>
        </>
        : <KeduLoading />}
    </div>
  );
};

export default TrainerList;
